/**
 * Native-shell app glue (Sprints 1–2): OAuth handoff, deep links, push.
 *
 * Runs only inside the Capacitor app (guarded by isNative()); inert on the
 * regular web. Owns the listeners that make the shell behave like an app:
 *
 *  - `stagster://auth?code=…`  → POST /api/auth/native-token → JWT stored
 *  - universal links / push taps → route the WebView to the target screen
 *  - push registration → POST /api/push/subscribe-native
 */
import { isNative, getNativePlatform, setNativeToken } from "./nativeBridge";
import { apiCall } from "../services/api/core";

/** Exchange a one-time Google auth code for a session JWT (Sprint 1). */
export async function exchangeNativeAuthCode(code) {
  const res = await fetch(`${window.location.origin}/api/auth/native-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null))?.detail;
    throw new Error(detail || "Invalid or expired auth code");
  }
  const data = await res.json();
  if (data?.token) {
    await setNativeToken(data.token);
  }
  return data;
}

/**
 * Sign in with Apple (native): runs the system ASAuthorization flow via the
 * Capacitor community plugin, returning the identity token for server-side
 * verification (`POST /api/auth/apple`). The token's `aud` is the app bundle
 * id — the backend accepts it alongside the web Services ID.
 */
export async function appleNativeSignIn({ clientId, nonce } = {}) {
  const plugin = window.Capacitor?.Plugins?.SignInWithApple;
  if (!plugin?.authorize) {
    throw new Error("Sign in with Apple is not available on this device");
  }
  const result = await plugin.authorize({
    clientId,
    redirectURI: window.location.origin,
    scopes: "email name",
    nonce,
  });
  const r = result?.response;
  const identityToken = r?.identityToken;
  if (!identityToken) {
    throw new Error("Missing Apple identity token");
  }
  return {
    identityToken,
    user: {
      name: {
        firstName: r?.givenName || "",
        lastName: r?.familyName || "",
      },
    },
  };
}

/** Register the device token for native push (Sprint 2). */
export async function registerNativePushToken(token) {
  if (!token) return false;
  const platform = getNativePlatform(); // "ios" | "android"
  const kind = platform === "android" ? "fcm" : "apns";
  try {
    const result = await apiCall("/push/subscribe-native", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_token: token, kind, platform }),
    });
    return Boolean(result && (result.status === "created" || result.status === "updated"));
  } catch (err) {
    console.warn("[native] push registration failed", err);
    return false;
  }
}

/**
 * Wire up native listeners. Returns a cleanup function. `onTokenStored` is
 * called with `{ user }` after a successful OAuth handoff; `onDeepLink` with a
 * parsed URL; `onPushRegistered` with the device token.
 */
export function setupNativeApp({ onTokenStored, onDeepLink, onPushRegistered } = {}) {
  if (!isNative()) return () => {};
  const C = window.Capacitor;
  const listeners = [];
  const add = (plugin, event, cb) => {
    try {
      const handle = plugin?.addListener?.(event, cb);
      if (handle) listeners.push(handle);
    } catch (err) {
      console.warn(`[native] addListener(${event}) failed`, err);
    }
  };

  // OAuth handoff: the web callback redirects to stagster://auth?code=…&next=…
  add(C.Plugins.App, "appUrlOpen", (data) => {
    try {
      const url = new URL(data?.url);
      if (url.protocol === "stagster:" && url.hostname === "auth") {
        const code = url.searchParams.get("code");
        const next = url.searchParams.get("next") || "/";
        if (code) {
          exchangeNativeAuthCode(code)
            .then((payload) => onTokenStored?.(payload, next))
            .catch((err) => console.error("[native] token exchange failed", err));
        }
        return;
      }
      // Universal links (https://www.stagster.com/…)
      if (url.protocol === "https:") {
        onDeepLink?.(url);
      }
    } catch (err) {
      console.warn("[native] appUrlOpen parse failed", err);
    }
  });

  // Push notifications (Sprint 2)
  const PN = C.Plugins.PushNotifications;
  if (PN) {
    add(PN, "registration", (data) => onPushRegistered?.(data?.value));
    add(PN, "registrationError", (err) =>
      console.error("[native] push registration error", err?.error),
    );
    add(PN, "pushNotificationActionPerformed", (notification) => {
      const url = notification?.notification?.data?.url;
      if (url) {
        try {
          onDeepLink?.(new URL(url, window.location.origin));
        } catch (err) {
          console.warn("[native] push tap URL parse failed", err);
        }
      }
    });
    // Keep the plugin's listener count stable; payload display is handled by iOS/Android.
    add(PN, "pushNotificationReceived", () => {});
  }

  return () => {
    listeners.forEach((handle) => {
      try {
        handle?.remove?.();
      } catch {
        // already removed
      }
    });
  };
}
